import { useState, useEffect, useRef, useCallback } from 'react'

export interface TradeTick {
  sequence_number: number
  instrument: { 0: string } | string
  price: string | number
  quantity: string | number
  side: 'BUY' | 'SELL'
  source_timestamp: string
  received_timestamp: string
}

export interface Quote {
  sequence_number: number
  instrument: { 0: string } | string
  bid_price: string | number
  bid_size: string | number
  ask_price: string | number
  ask_size: string | number
  timestamp: string
}

export interface Candle {
  instrument: { 0: string } | string
  timeframe: 'S1' | 'S5' | 'S15' | 'M1' | 'M5'
  open: string | number
  high: string | number
  low: string | number
  close: string | number
  volume: string | number
  open_time: string
  close_time: string
  ticks_count: number
  is_closed: boolean
}

export interface MarketStatus {
  mode: 'REALTIME' | 'DELAYED' | 'FROZEN' | 'REPLAY' | 'SYNTHETIC' | 'UNKNOWN'
  active_symbol: { 0: string } | string
  connected: boolean
  feed_running: boolean
  events_received: number
  events_lost: number
  estimated_delay_ms: number
  last_timestamp: string
}

export interface PositionRecord {
  position_id: string
  instrument: { 0: string } | string
  side: 'BUY' | 'SELL'
  quantity: string | number
  entry_price: string | number
  current_price: string | number
  unrealized_pnl: string | number
  realized_pnl: string | number
  opened_at: string
}

export interface ExecutionRecord {
  execution_id: string
  client_order_id: string
  instrument: { 0: string } | string
  side: 'BUY' | 'SELL'
  price: string | number
  quantity: string | number
  timestamp: string
}

export interface AccountState {
  account_id: string
  initial_capital: string | number
  current_capital: string | number
  realized_pnl: string | number
  unrealized_pnl: string | number
  open_positions_count: number
  total_trades_count: number
  winning_trades_count: number
  losing_trades_count: number
}

/** One sample of account equity, accumulated client-side as the engine reports. */
export interface EquityPoint {
  t: number
  equity: number
  realized: number
  /** Cumulative counters, so any window can be derived by differencing. */
  trades: number
  wins: number
  losses: number
}

export interface Block {
  direction: 'UP' | 'DOWN'
  length: number
  high: string
  low: string
  open: string
  close: string
  start_time: string
  end_time: string
  is_live: boolean
}

export interface BlockStats {
  total: number
  up_count: number
  down_count: number
  up_max_length: number
  down_max_length: number
  up_mean_length: string
  down_mean_length: string
}

export interface Leg {
  direction: 'UP' | 'DOWN'
  from_price: string
  to_price: string
  from_time: string
  to_time: string
  steps: number
  is_confirmed: boolean
}

export interface IndicatorSnapshot {
  instrument: string
  timeframe: 'S1' | 'S5' | 'S15' | 'M1' | 'M5'
  candles: number
  blocks: { blocks: Block[]; stats: BlockStats }
  steps: { finest_step: string; step: string; density: number; legs: Leg[] }
}

export interface NewsItem {
  provider: string
  article_id: string
  headline: string
  /** Absent when the feed omits a link; the CTA is hidden rather than dead. */
  url?: string
  timestamp: string
}

export type MarketEvent =
  | { type: 'Tick'; payload: TradeTick }
  | { type: 'Quote'; payload: Quote }
  | { type: 'Candle'; payload: Candle }
  | { type: 'Status'; payload: MarketStatus }
  | { type: 'AccountUpdated'; payload: AccountState }
  | { type: 'PositionUpdated'; payload: PositionRecord[] }
  | { type: 'ExecutionOccurred'; payload: ExecutionRecord }
  | { type: 'OrderRejected'; payload: { client_order_id: string; decision: RiskDecision } }
  | { type: 'Indicators'; payload: IndicatorSnapshot }
  | { type: 'News'; payload: NewsItem }

export type RiskDecision =
  | { decision: 'ACCEPTED' }
  | { decision: 'REJECTED'; reason: string; detail: string }

/** Per-instrument quote state; the engine streams several at once. */
export interface SymbolQuote {
  lastPrice: number
  bidPrice: number
  askPrice: number
  spread: number
  ticksCount: number
}

const emptyQuote = (): SymbolQuote => ({
  lastPrice: 0,
  bidPrice: 0,
  askPrice: 0,
  spread: 0,
  ticksCount: 0,
})

/** Instruments carry a plain symbol; older payloads wrapped it in a tuple. */
const symbolOf = (instrument: { 0: string } | string): string =>
  typeof instrument === 'string' ? instrument : instrument[0]

export interface TradeViewState {
  connected: boolean
  dataMode: 'REALTIME' | 'DELAYED' | 'FROZEN' | 'REPLAY' | 'SYNTHETIC' | 'UNKNOWN'
  /** Every instrument the engine has announced. */
  symbols: string[]
  selectedSymbol: string
  bySymbol: Record<string, SymbolQuote>
  candles: Candle[]
  recentTicks: TradeTick[]
  marketStatus: MarketStatus | null
  account: AccountState
  positions: PositionRecord[]
  executions: ExecutionRecord[]
  equityCurve: EquityPoint[]
  /** One analysis per instrument: merging two series would invent structure. */
  indicators: Record<string, IndicatorSnapshot>
  /** Newest first, capped: a newsletter is read, not accumulated forever. */
  news: NewsItem[]
  /** Mirrors the engine, not the click: the button shows what is really running. */
  feedRunning: boolean
  /** When the shown data was last confirmed live. Null once the engine speaks. */
  frozenSince: string | null
  lastError: string | null
}

/** Bars retained per timeframe, independently of the other series. */
const CANDLES_PER_TIMEFRAME = 300

const CACHE_KEY = 'tradeview.lastSession.v1'

/** A week of world news is what a trader reads; older is weight without value. */
const NEWS_RETENTION_DAYS = 7

/**
 * Drops headlines past the retention window.
 *
 * Applied on both read and write: the engine already filters at the source, but
 * a cache written days ago would otherwise keep resurfacing what it holds, and
 * grow without bound across sessions.
 */
function withinRetention(items: NewsItem[]): NewsItem[] {
  const cutoff = Date.now() - NEWS_RETENTION_DAYS * 86_400_000
  return items.filter((item) => {
    const published = Date.parse(item.timestamp)
    // An unreadable date is kept: feeds that omit it are not publishing old
    // news, and discarding them would silently lose whole sources.
    return Number.isNaN(published) || published >= cutoff
  })
}

/** What survives the engine being stopped, and when it was last true. */
interface CachedSession {
  savedAt: string
  news: NewsItem[]
  candles: Candle[]
  indicators: Record<string, IndicatorSnapshot>
  bySymbol: Record<string, SymbolQuote>
  symbols: string[]
  dataMode: TradeViewState['dataMode']
}

function readCache(): CachedSession | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const session = JSON.parse(raw) as CachedSession
    return { ...session, news: withinRetention(session.news ?? []) }
  } catch {
    // A corrupt cache must never stop the app from starting.
    return null
  }
}

function writeCache(session: CachedSession) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(session))
  } catch {
    // Quota exceeded or storage disabled: the live session is unaffected.
  }
}

const num = (value: string | number) =>
  typeof value === 'string' ? parseFloat(value) : value

export function useTradeViewWebSocket(url: string = 'ws://localhost:8080/ws') {
  const [state, setState] = useState<TradeViewState>(() => {
    const cached = readCache()
    return {
      connected: false,
      dataMode: cached?.dataMode ?? 'SYNTHETIC',
      // Placeholder only: the engine announces the instruments it is running.
      symbols: cached?.symbols ?? [],
      selectedSymbol: cached?.symbols?.[0] ?? 'MES',
      bySymbol: cached?.bySymbol ?? {},
      candles: cached?.candles ?? [],
      recentTicks: [],
      marketStatus: null,
    account: {
      account_id: 'demo-paper-100k',
      // Placeholder until the engine reports; it owns the real funding amount.
      initial_capital: 39000,
      current_capital: 39000,
      realized_pnl: 0,
      unrealized_pnl: 0,
      open_positions_count: 0,
      total_trades_count: 0,
      winning_trades_count: 0,
      losing_trades_count: 0,
    },
      positions: [],
      executions: [],
      equityCurve: [],
      indicators: cached?.indicators ?? {},
      news: cached?.news ?? [],
      feedRunning: false,
      // Anything restored is frozen until the engine speaks again.
      frozenSince: cached?.savedAt ?? null,
      lastError: null,
    }
  })

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intentionalCloseRef = useRef(false)

  const clearReconnect = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }

  const connect = useCallback(() => {
    // Never stack sockets: React re-mounts (StrictMode) and stale reconnect
    // timers would otherwise churn connections, leaving `wsRef` pointing at a
    // socket that is still CONNECTING when a command is sent.
    const existing = wsRef.current
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    try {
      intentionalCloseRef.current = false
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        clearReconnect()
        // Live again: what is on screen is no longer a snapshot.
        setState((prev) => ({ ...prev, connected: true, frozenSince: null }))
      }

      ws.onmessage = (event) => {
        try {
          const parsed: MarketEvent = JSON.parse(event.data)

          if (parsed.type === 'Tick') {
            const tick = parsed.payload
            const p = typeof tick.price === 'string' ? parseFloat(tick.price) : tick.price
            const instrument = symbolOf(tick.instrument)
            setState((prev) => {
              const current = prev.bySymbol[instrument] ?? emptyQuote()
              return {
                ...prev,
                bySymbol: {
                  ...prev.bySymbol,
                  [instrument]: {
                    ...current,
                    lastPrice: p,
                    ticksCount: current.ticksCount + 1,
                  },
                },
                recentTicks: [tick, ...prev.recentTicks.slice(0, 49)],
              }
            })
          } else if (parsed.type === 'Quote') {
            const q = parsed.payload
            const bp = typeof q.bid_price === 'string' ? parseFloat(q.bid_price) : q.bid_price
            const ap = typeof q.ask_price === 'string' ? parseFloat(q.ask_price) : q.ask_price
            const sp = parseFloat((ap - bp).toFixed(4))
            const instrument = symbolOf(q.instrument)
            setState((prev) => {
              const current = prev.bySymbol[instrument] ?? emptyQuote()
              return {
                ...prev,
                bySymbol: {
                  ...prev.bySymbol,
                  [instrument]: {
                    ...current,
                    bidPrice: bp,
                    askPrice: ap,
                    spread: Math.max(0, sp),
                  },
                },
              }
            })
          } else if (parsed.type === 'Candle') {
            const candle = parsed.payload
            setState((prev) => {
              const existingIdx = prev.candles.findIndex(
                (c) => c.open_time === candle.open_time && c.timeframe === candle.timeframe
              )
              let nextCandles = [...prev.candles]
              if (existingIdx >= 0) {
                nextCandles[existingIdx] = candle
              } else {
                nextCandles.push(candle)
                // Trim per timeframe. A shared cap lets the 1-second stream,
                // which arrives far more often, evict the slower series the
                // chart is actually drawing.
                const sameTimeframe = nextCandles.filter((c) => c.timeframe === candle.timeframe)
                if (sameTimeframe.length > CANDLES_PER_TIMEFRAME) {
                  const cutoff = sameTimeframe[sameTimeframe.length - CANDLES_PER_TIMEFRAME]
                  nextCandles = nextCandles.filter(
                    (c) => c.timeframe !== candle.timeframe || c.open_time >= cutoff.open_time
                  )
                }
              }
              return { ...prev, candles: nextCandles }
            })
          } else if (parsed.type === 'Status') {
            const st = parsed.payload
            const announced = symbolOf(st.active_symbol)
            setState((prev) => {
              const symbols = prev.symbols.includes(announced)
                ? prev.symbols
                : [...prev.symbols, announced].sort()
              return {
                ...prev,
                dataMode: st.mode,
                marketStatus: st,
                feedRunning: st.feed_running ?? prev.feedRunning,
                symbols,
                // Announcements register an instrument; they never move the
                // operator's selection out from under them.
                selectedSymbol: prev.symbols.length === 0 ? announced : prev.selectedSymbol,
              }
            })
          } else if (parsed.type === 'AccountUpdated') {
            const acc = parsed.payload
            setState((prev) => {
              const realized = num(acc.realized_pnl)
              const equity = num(acc.current_capital) + num(acc.unrealized_pnl)
              const last = prev.equityCurve[prev.equityCurve.length - 1]

              // Only sample when the account actually moved, so the curve does
              // not grow by one point per incoming tick.
              const moved =
                !last || last.equity !== equity || last.realized !== realized
              if (!moved) return { ...prev, account: acc }

              const point: EquityPoint = {
                t: Date.now(),
                equity,
                realized,
                trades: acc.total_trades_count,
                wins: acc.winning_trades_count,
                losses: acc.losing_trades_count,
              }
              const curve = [...prev.equityCurve, point]
              return {
                ...prev,
                account: acc,
                equityCurve: curve.length > 5000 ? curve.slice(-5000) : curve,
              }
            })
          } else if (parsed.type === 'PositionUpdated') {
            const pos = parsed.payload
            setState((prev) => ({ ...prev, positions: pos }))
          } else if (parsed.type === 'ExecutionOccurred') {
            const exec = parsed.payload
            setState((prev) => ({
              ...prev,
              executions: [exec, ...prev.executions.slice(0, 49)],
            }))
          } else if (parsed.type === 'Indicators') {
            const snapshot = parsed.payload
            const instrument = symbolOf(snapshot.instrument as unknown as string)
            setState((prev) => ({
              ...prev,
              indicators: { ...prev.indicators, [instrument]: snapshot },
            }))
          } else if (parsed.type === 'News') {
            const item = parsed.payload
            setState((prev) => ({
              ...prev,
              news: withinRetention([item, ...prev.news]).slice(0, 200),
            }))
          } else if (parsed.type === 'OrderRejected') {
            const { decision } = parsed.payload
            const message =
              decision.decision === 'REJECTED'
                ? `${decision.reason}: ${decision.detail}`
                : 'Order rejected'
            setState((prev) => ({ ...prev, lastError: message }))
          }
        } catch {
          // Ignore parse errors
        }
      }

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null
        setState((prev) => ({
          ...prev,
          connected: false,
          // Dated from now: this is when the figures stopped being true.
          frozenSince: prev.frozenSince ?? new Date().toISOString(),
        }))
        if (intentionalCloseRef.current) return
        clearReconnect()
        reconnectTimerRef.current = setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        ws.close()
      }
    } catch {
      clearReconnect()
      reconnectTimerRef.current = setTimeout(connect, 5000)
    }
  }, [url])

  /** Sends a command, surfacing the failure rather than dropping it silently. */
  const send = useCallback((payload: object, description: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setState((prev) => ({
        ...prev,
        lastError: `${description} non envoyé : moteur non connecté`,
      }))
      return false
    }
    ws.send(JSON.stringify(payload))
    return true
  }, [])

  // Orders follow the instrument on screen, so a click can never land on a
  // series the operator is not looking at.
  const symbolRef = useRef(state.selectedSymbol)
  symbolRef.current = state.selectedSymbol

  const placeOrder = useCallback(
    (side: 'BUY' | 'SELL', quantity: number = 100) => {
      send(
        {
          action: 'PlaceOrder',
          payload: {
            client_order_id: `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            instrument: symbolRef.current,
            side,
            order_type: 'MARKET',
            price: null,
            quantity: quantity.toString(),
          },
        },
        `Ordre ${side}`
      )
    },
    [send]
  )

  const closePosition = useCallback(
    (symbol?: string) => {
      send(
        { action: 'ClosePosition', payload: { symbol: symbol ?? symbolRef.current } },
        'Fermeture de position'
      )
    },
    [send]
  )

  const setMarketFeed = useCallback(
    (running: boolean) => {
      send(
        { action: 'SetMarketFeed', payload: { running } },
        running ? 'Démarrage du marché' : 'Arrêt du marché'
      )
    },
    [send]
  )

  const resetAccount = useCallback(() => {
    send({ action: 'ResetAccount', payload: {} }, 'Réinitialisation du compte')
  }, [send])

  // Kept in a ref so the saver reads the latest state without restarting its
  // timer on every tick.
  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    const save = () => {
      const current = stateRef.current
      // Nothing worth restoring yet, and an empty write would erase a good
      // snapshot from an earlier session.
      if (current.news.length === 0 && current.candles.length === 0) return

      writeCache({
        savedAt: new Date().toISOString(),
        news: withinRetention(current.news),
        candles: current.candles,
        indicators: current.indicators,
        bySymbol: current.bySymbol,
        symbols: current.symbols,
        dataMode: current.dataMode,
      })
    }

    const timer = setInterval(save, 10_000)
    window.addEventListener('beforeunload', save)
    return () => {
      clearInterval(timer)
      window.removeEventListener('beforeunload', save)
      save()
    }
  }, [])

  useEffect(() => {
    connect()
    return () => {
      clearReconnect()
      intentionalCloseRef.current = true
      wsRef.current?.close()
    }
  }, [connect])

  const selectSymbol = useCallback((symbol: string) => {
    setState((prev) => ({ ...prev, selectedSymbol: symbol }))
  }, [])

  // Views read one instrument at a time. Exposing the selected one under the
  // old flat names keeps every component honest about which series it draws,
  // without each of them reaching into the per-symbol maps.
  const selected = state.bySymbol[state.selectedSymbol] ?? emptyQuote()

  return {
    ...state,
    symbol: state.selectedSymbol,
    lastPrice: selected.lastPrice,
    bidPrice: selected.bidPrice,
    askPrice: selected.askPrice,
    spread: selected.spread,
    ticksCount: selected.ticksCount,
    /** Analysis of the selected instrument only. */
    indicators: state.indicators[state.selectedSymbol] ?? null,
    indicatorsBySymbol: state.indicators,
    placeOrder,
    closePosition,
    resetAccount,
    setMarketFeed,
    selectSymbol,
  }
}
